// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TooltipPresentationProps } from "@synd/MobiusOverrides/Tooltip/Tooltip";
import QuestionCircle from "@synd/XDL/Icons/QuestionCircle";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { avoidBreakInsidePrintElement, color, mergeWithCss } from "@synd/XDL/Utilities";
import { css } from "styled-components";

interface OrderTotalDisplaySharedStyles {
    mainContainer: GridContainerProps;
    label: TypographyPresentationProps;
    value: TypographyPresentationProps;
    labelGridItem: GridItemProps;
    valueGridItem: GridItemProps;
    totalLabel?: TypographyPresentationProps;
    totalValue?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    microCopyText?: TypographyPresentationProps;
    styledHr?: InjectableCss;
    wrapper?: InjectableCss;
    shippingTooltipIcon?: TooltipPresentationProps;
}

const label: TypographyPresentationProps = {
    variant: "h6",
    color: "text",
    forwardAs: "span",
    transform: "none",
    weight: fontConfig.weight.regular,
    css: css`
        margin-bottom: 0;
        letter-spacing: 0;
    `,
};
const value: TypographyPresentationProps = {
    variant: "h6",
    color: "text",
    forwardAs: "span",
    transform: "none",
    css: css`
        margin-bottom: 0;
        margin-left: auto;
        letter-spacing: 0;
    `,
};
export const orderTotalDisplaySharedStyles: OrderTotalDisplaySharedStyles = {
    mainContainer: {
        gap: 0,
        offsetCss: css`
            row-gap: 8px;
            @media print {
                display: block;
            }
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            margin: 0;
        `,
    },
    labelGridItem: {
        width: 7,
        css: css`
            align-items: center;
            @media print {
                &&& {
                    margin-bottom: 8px;
                }
            }
        `,
    },
    valueGridItem: {
        css: css`
            @media print {
                text-align: right;
                &&& {
                    margin-bottom: 8px;
                }
            }
        `,
        width: 5,
    },
    label: {
        ...label,
    },
    value: {
        ...value,
    },
    totalLabel: {
        ...mergeWithCss(label, {
            ...fontConfig.regularSingleLine,
        }),
    },
    totalValue: {
        ...mergeWithCss(value, {
            ...fontConfig.regularSingleLine,
        }),
    },
    styledHr: {
        css: css`
            width: 100%;
            border: none;
            border-bottom: 1px solid ${color("common.border")};
            margin: 4px 0;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            ${avoidBreakInsidePrintElement}
        `,
    },
    microCopyText: {
        ...fontConfig.xSmall,
        css: css`
            display: flex;
            padding-top: 16px;
            column-gap: 4px;
            flex-wrap: nowrap;
            border-top: 1px solid ${color("common.border")};
            &::before {
                content: "*";
                color: ${color("danger.main")};
            }
        `,
    },
    shippingTooltipIcon:{
        mergeCss: true,
            extendedStyles: {
                triggerWrapper: {
                    css: css`
                        padding-top: 4px;
                    `,
                },
            },
            iconProps: {
                mergeCss: true,
                src: QuestionCircle,
                size: 12,
                css: css`
                    svg {
                        height: 13px;
                    }
                `,
            },
    }
};
